/**
 * 電話番号・メールアドレスの厳格バリデーション（予約フィードバック3）。
 * BE（Bean Validation）と同一ルール。FE は blur 時+送信時に検証し、
 * 送信前に正規化（全角→半角・ハイフン/スペース除去）した値を送る。
 * 契約: reservation-phase1-feedback3（電話 ^0\d{9,10}$ | ^\+81\d{9,10}$、メールは pattern+ドット規則）。
 */

/** 全角数字・全角プラス・全角英字を半角へ正規化する */
const toHalfWidth = (value: string): string =>
  value
    .replace(/[０-９ａ-ｚＡ-Ｚ]/g, (ch) => String.fromCharCode(ch.charCodeAt(0) - 0xfee0))
    .replace(/＋/g, '+')
    .replace(/＠/g, '@');

/**
 * 電話番号の正規化。全角→半角のうえ、ハイフン類（-‐‑–—ー−）・スペース・括弧を除去する。
 * 例: '０９０-１２３４ ５６７８' → '09012345678'
 */
export const normalizePhoneNumber = (value: string): string =>
  toHalfWidth(value.trim()).replace(/[-‐‑–—ー−()（）\s]/g, '');

/** 正規化後の電話番号形式（日本）: 0始まり10〜11桁 または +81 + 9〜10桁 */
const PHONE_RE = /^0\d{9,10}$/;
const PHONE_INTL_RE = /^\+81\d{9,10}$/;

/** 電話番号として妥当か（正規化してから判定。空文字は false） */
export const isValidPhoneNumber = (value: string): boolean => {
  const normalized = normalizePhoneNumber(value);
  return PHONE_RE.test(normalized) || PHONE_INTL_RE.test(normalized);
};

/** メールの基本形式（BE の @Email 補強 pattern と同一） */
const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

/** メールの正規化（全角→半角・前後空白除去） */
export const normalizeEmail = (value: string): string => toHalfWidth(value.trim());

/**
 * メールアドレスとして妥当か（正規化してから判定。空文字は false）。
 * 基本 pattern に加え、連続ドット・ローカル部/ドメインの先頭末尾ドットを拒否する。
 */
export const isValidEmail = (value: string): boolean => {
  const normalized = normalizeEmail(value);
  if (!EMAIL_RE.test(normalized)) return false;
  if (normalized.includes('..')) return false;
  const [local, domain] = normalized.split('@');
  if (local.startsWith('.') || local.endsWith('.')) return false;
  if (domain.startsWith('.') || domain.endsWith('.') || domain.startsWith('-')) return false;
  return true;
};

/** 管理画面（日本語のみ）用のエラー文言。公開ページは publicReservationI18n の辞書を使う */
export const PHONE_FORMAT_ERROR = '電話番号の形式が正しくありません（例: 090-1234-5678）';
export const EMAIL_FORMAT_ERROR = 'メールアドレスの形式が正しくありません（例: mail@example.com）';
